import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { AttachmentSection } from '../components/AttachmentSection';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Flame, Plus, Calendar, TrendingUp, AlertTriangle, CheckCircle, 
  ChevronDown, ChevronUp, X, BarChart3, Lightbulb, Heart, GitCompare
} from 'lucide-react';
import { toast } from 'sonner';
import PhotoCompareDialog from '../components/PhotoCompareDialog';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

export const FlareJournal = () => {
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [currentFlare, setCurrentFlare] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: todayLocal(),
    child_name: '',
    severity: 7,
    symptoms: [],
    behaviors: [],
    recent_illness: '',
    recent_stress: '',
    sleep_quality: '',
    dietary_changes: '',
    other_triggers: '',
    what_helped: '',
    what_didnt_help: '',
    parent_coping: '',
    notes: '',
    is_new_flare: true
  });

  const commonSymptoms = [
    'OCD thoughts', 'Anxiety', 'Rage', 'Tics', 'Separation anxiety',
    'Sleep problems', 'Regression', 'Food restriction', 'Urinary issues',
    'Sensory sensitivity', 'Brain fog', 'Fatigue', 'Joint pain', 'Headaches'
  ];

  const commonBehaviors = [
    'Aggression', 'Defiance', 'Crying spells', 'Clinginess', 'Withdrawal',
    'Repetitive questions', 'Rituals', 'Perfectionism', 'Meltdowns',
    'School refusal', 'Difficulty transitioning', 'Hyperactivity'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entriesRes, patternsRes, currentRes] = await Promise.all([
        axios.get(`${API}/flare-journal`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/flare-journal/patterns`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/flare-journal/current`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEntries(entriesRes.data);
      setPatterns(patternsRes.data);
      setCurrentFlare(currentRes.data);
    } catch (error) {
      console.error('Error fetching flare journal:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSymptom = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const toggleBehavior = (behavior) => {
    setFormData(prev => ({
      ...prev,
      behaviors: prev.behaviors.includes(behavior)
        ? prev.behaviors.filter(b => b !== behavior)
        : [...prev.behaviors, behavior]
    }));
  };

  const submitEntry = async () => {
    if (formData.symptoms.length === 0 && formData.behaviors.length === 0) {
      toast.error('Please select at least one symptom or behavior');
      return;
    }

    try {
      await axios.post(`${API}/flare-journal`, {
        ...formData,
        flare_start_date: formData.is_new_flare ? formData.date : (currentFlare?.flare_start_date || formData.date)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Flare entry saved');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const endFlare = async (entryId) => {
    try {
      await axios.post(`${API}/flare-journal/${entryId}/end-flare`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flare marked as ended');
      fetchData();
    } catch (error) {
      toast.error('Failed to end flare');
    }
  };

  const resetForm = () => {
    setFormData({
      date: todayLocal(),
      child_name: '',
      severity: 7,
      symptoms: [],
      behaviors: [],
      recent_illness: '',
      recent_stress: '',
      sleep_quality: '',
      dietary_changes: '',
      other_triggers: '',
      what_helped: '',
      what_didnt_help: '',
      parent_coping: '',
      notes: '',
      is_new_flare: !currentFlare
    });
  };

  const getSeverityColor = (severity) => {
    if (severity <= 3) return 'bg-green-500';
    if (severity <= 5) return 'bg-yellow-500';
    if (severity <= 7) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const formatDate = (dateStr) => {
    return formatLocalDate(dateStr, 'en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="flare-journal">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Flame className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">Flare Journal</h1>
            </div>
            <p className="text-[#57534E]">
              Document intense moments to identify patterns and track what helps
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCompareOpen(true)}
              className="rounded-full px-4 flex items-center gap-2"
              data-testid="compare-flare-photos-btn"
            >
              <GitCompare className="w-4 h-4" />
              Compare Photos
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-orange-500 text-white hover:bg-orange-600 rounded-full px-6 flex items-center gap-2"
              data-testid="new-entry-btn"
            >
              <Plus className="w-5 h-5" />
              New Entry
            </Button>
          </div>
        </div>
      </div>

      {/* Current Flare Alert */}
      {currentFlare && (
        <Card className="bg-orange-50 border-2 border-orange-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              <div>
                <p className="font-semibold text-orange-800">Ongoing Flare</p>
                <p className="text-sm text-orange-700">
                  Started {formatDate(currentFlare.flare_start_date)} • 
                  {currentFlare.child_name && ` ${currentFlare.child_name} • `}
                  Last severity: {currentFlare.severity}/10
                </p>
              </div>
            </div>
            <Button
              onClick={() => endFlare(currentFlare.id)}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-100 rounded-full"
              data-testid="end-flare-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Flare Ended
            </Button>
          </div>
        </Card>
      )}

      <Tabs defaultValue="entries" className="space-y-6">
        <TabsList className="bg-white rounded-xl border border-stone-200 p-1">
          <TabsTrigger value="entries" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white px-6">
            <Calendar className="w-4 h-4 mr-2" />
            Journal Entries
          </TabsTrigger>
          <TabsTrigger value="patterns" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white px-6">
            <BarChart3 className="w-4 h-4 mr-2" />
            Patterns & Insights
          </TabsTrigger>
        </TabsList>

        {/* Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A8A29E]">Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <Flame className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-2">No flare entries yet</p>
              <p className="text-[#A8A29E] mb-6">
                Start documenting during intense moments to build your pattern library
              </p>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-500 text-white hover:bg-orange-600 rounded-full"
              >
                Create First Entry
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <Card 
                  key={entry.id}
                  className={`bg-white rounded-xl border ${entry.is_flare_ongoing ? 'border-orange-200' : 'border-stone-100'} shadow-sm`}
                >
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-1.5 ${getSeverityColor(entry.severity)}`} />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-[#2D2D2D]">{formatDate(entry.date)}</span>
                            {entry.child_name && (
                              <span className="text-sm text-[#57534E]">• {entry.child_name}</span>
                            )}
                            {entry.is_flare_ongoing && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                Ongoing
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#57534E]">
                            Severity: {entry.severity}/10 • 
                            {entry.symptoms.length} symptoms • 
                            {entry.behaviors.length} behaviors
                          </p>
                        </div>
                      </div>
                      {expandedEntry === entry.id ? (
                        <ChevronUp className="w-5 h-5 text-[#A8A29E]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#A8A29E]" />
                      )}
                    </div>
                  </div>
                  
                  {expandedEntry === entry.id && (
                    <div className="px-4 pb-4 border-t border-stone-100 pt-4 space-y-4">
                      {entry.symptoms.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-[#57534E] mb-2">Symptoms:</p>
                          <div className="flex flex-wrap gap-2">
                            {entry.symptoms.map((s, i) => (
                              <span key={i} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {entry.behaviors.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-[#57534E] mb-2">Behaviors:</p>
                          <div className="flex flex-wrap gap-2">
                            {entry.behaviors.map((b, i) => (
                              <span key={i} className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm">
                                {b}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        {entry.recent_illness && (
                          <div><strong>Recent illness:</strong> {entry.recent_illness}</div>
                        )}
                        {entry.recent_stress && (
                          <div><strong>Recent stress:</strong> {entry.recent_stress}</div>
                        )}
                        {entry.sleep_quality && (
                          <div><strong>Sleep:</strong> {entry.sleep_quality}</div>
                        )}
                        {entry.dietary_changes && (
                          <div><strong>Diet changes:</strong> {entry.dietary_changes}</div>
                        )}
                      </div>
                      
                      {entry.what_helped && (
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-green-800 mb-1">What helped:</p>
                          <p className="text-sm text-green-700">{entry.what_helped}</p>
                        </div>
                      )}
                      
                      {entry.notes && (
                        <div className="bg-stone-50 rounded-lg p-3">
                          <p className="text-sm text-[#57534E]">{entry.notes}</p>
                        </div>
                      )}

                      <div className="pt-3 border-t border-stone-100">
                        <AttachmentSection
                          parentType="flare"
                          parentId={entry.id}
                          token={token}
                          title="Photos & Documents"
                        />
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Patterns Tab */}
        <TabsContent value="patterns" className="space-y-6">
          {!patterns || patterns.total_entries === 0 ? (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <TrendingUp className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-2">No patterns to show yet</p>
              <p className="text-[#A8A29E]">
                Document multiple flare entries to start seeing patterns
              </p>
            </Card>
          ) : (
            <>
              {/* Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                  <p className="text-3xl font-bold text-[#2D2D2D]">{patterns.total_entries}</p>
                  <p className="text-sm text-[#57534E]">Total Entries</p>
                </Card>
                <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                  <p className="text-3xl font-bold text-[#2D2D2D]">{patterns.total_unique_flares}</p>
                  <p className="text-sm text-[#57534E]">Unique Flares</p>
                </Card>
                <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                  <p className="text-3xl font-bold text-orange-500">{patterns.ongoing_flares}</p>
                  <p className="text-sm text-[#57534E]">Ongoing</p>
                </Card>
                <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                  <p className="text-3xl font-bold text-[#2D2D2D]">{patterns.average_severity}/10</p>
                  <p className="text-sm text-[#57534E]">Avg Severity</p>
                </Card>
              </div>

              {/* Top Symptoms & Behaviors */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-white rounded-xl border border-stone-100 p-6">
                  <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4">Most Common Symptoms</h3>
                  {patterns.top_symptoms?.length > 0 ? (
                    <div className="space-y-2">
                      {patterns.top_symptoms.map((item, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[#57534E]">{item.symptom}</span>
                          <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            {item.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#A8A29E]">No data yet</p>
                  )}
                </Card>

                <Card className="bg-white rounded-xl border border-stone-100 p-6">
                  <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4">Most Common Behaviors</h3>
                  {patterns.top_behaviors?.length > 0 ? (
                    <div className="space-y-2">
                      {patterns.top_behaviors.map((item, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[#57534E]">{item.behavior}</span>
                          <span className="text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                            {item.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#A8A29E]">No data yet</p>
                  )}
                </Card>
              </div>

              {/* Common Triggers */}
              <Card className="bg-white rounded-xl border border-stone-100 p-6">
                <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Common Triggers Identified
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {patterns.common_triggers?.illness?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-[#57534E] mb-2">Illnesses:</p>
                      <div className="flex flex-wrap gap-2">
                        {patterns.common_triggers.illness.map((t, i) => (
                          <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {patterns.common_triggers?.stress?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-[#57534E] mb-2">Stressors:</p>
                      <div className="flex flex-wrap gap-2">
                        {patterns.common_triggers.stress.map((t, i) => (
                          <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* What Has Helped */}
              {patterns.what_has_helped?.length > 0 && (
                <Card className="bg-green-50 rounded-xl border border-green-200 p-6">
                  <h3 className="font-nunito font-bold text-green-800 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    What Has Helped During Flares
                  </h3>
                  <div className="space-y-2">
                    {patterns.what_has_helped.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-green-800">{item}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Entry Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Document This Moment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Child's Name (optional)</label>
                <Input
                  value={formData.child_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, child_name: e.target.value }))}
                  placeholder="Enter name..."
                  className="mt-1"
                />
              </div>
            </div>

            {/* Flare Status */}
            {currentFlare && (
              <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!formData.is_new_flare}
                    onChange={() => setFormData(prev => ({ ...prev, is_new_flare: false }))}
                  />
                  <span>Continue current flare</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.is_new_flare}
                    onChange={() => setFormData(prev => ({ ...prev, is_new_flare: true }))}
                  />
                  <span>This is a new flare</span>
                </label>
              </div>
            )}

            {/* Severity */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Severity: {formData.severity}/10</label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.severity}
                onChange={(e) => setFormData(prev => ({ ...prev, severity: parseInt(e.target.value) }))}
                className="w-full mt-2"
              />
              <div className="flex justify-between text-xs text-[#A8A29E]">
                <span>Mild</span>
                <span>Moderate</span>
                <span>Severe</span>
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Symptoms</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {commonSymptoms.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleSymptom(symptom)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      formData.symptoms.includes(symptom)
                        ? 'bg-red-500 text-white'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            {/* Behaviors */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Behaviors</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {commonBehaviors.map((behavior) => (
                  <button
                    key={behavior}
                    type="button"
                    onClick={() => toggleBehavior(behavior)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      formData.behaviors.includes(behavior)
                        ? 'bg-orange-500 text-white'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {behavior}
                  </button>
                ))}
              </div>
            </div>

            {/* Triggers */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-[#2D2D2D]">Potential Triggers</label>
              <Input
                value={formData.recent_illness}
                onChange={(e) => setFormData(prev => ({ ...prev, recent_illness: e.target.value }))}
                placeholder="Recent illness? (strep, cold, etc.)"
              />
              <Input
                value={formData.recent_stress}
                onChange={(e) => setFormData(prev => ({ ...prev, recent_stress: e.target.value }))}
                placeholder="Recent stressors?"
              />
              <Input
                value={formData.sleep_quality}
                onChange={(e) => setFormData(prev => ({ ...prev, sleep_quality: e.target.value }))}
                placeholder="How has sleep been?"
              />
              <Input
                value={formData.dietary_changes}
                onChange={(e) => setFormData(prev => ({ ...prev, dietary_changes: e.target.value }))}
                placeholder="Any dietary changes?"
              />
            </div>

            {/* What Helped */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">What helped (if anything)?</label>
              <Textarea
                value={formData.what_helped}
                onChange={(e) => setFormData(prev => ({ ...prev, what_helped: e.target.value }))}
                placeholder="Techniques, medications, approaches that helped..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* How Parent Is Coping */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary" />
                How are YOU doing right now?
              </label>
              <Textarea
                value={formData.parent_coping}
                onChange={(e) => setFormData(prev => ({ ...prev, parent_coping: e.target.value }))}
                placeholder="Your feelings matter too. How are you coping?"
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Additional Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Anything else to remember about this moment..."
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <Button
                onClick={submitEntry}
                className="flex-1 bg-orange-500 text-white hover:bg-orange-600 rounded-full"
              >
                Save Entry
              </Button>
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PhotoCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        token={token}
        parentType="flare"
        title="Compare Flare Photos"
        description="Track rash progression and physical signs across flare days. Photos are sorted oldest → newest by entry date."
      />
    </div>
  );
};

export default FlareJournal;
